<template>
  <v-card
    flat
    class="upload-card cfs-field !bg-transparent pb-5"
    rounded="lg"
    :class="{ readonly }"
  >
    <p class="input-title px-1 pb-1" :class="{ disabled }">
      {{ label }}
    </p>
    <v-card-title v-if="title" class="upload-card-title">{{ title }}</v-card-title>

    <CfsReadonlyFieldComponent v-if="readonly && !files.length" :value="modelValue" />

    <v-row v-else>
      <v-col v-if="!readonly && (multiple || !files.length)" cols="12" :sm="files.length ? 6 : 12">
        <v-card-text
          class="upload-content text-center"
          :class="[disabled && 'pointer-events-none opacity-50']"
          @drop.prevent="onDropFiles"
          @dragover.prevent="toggleDragOver(true)"
          @dragenter.prevent
          @dragleave.prevent="toggleDragOver(false)"
        >
          <div>
            <v-icon color="primary" size="3rem">
              {{ isDragOver ? 'mdi-cloud-upload' : 'mdi-cloud-upload-outline' }}
            </v-icon>
            <div>
              <h3>{{ isDragOver ? $t('DropFile') : $t('DragAndDropFile') }}</h3>
            </div>
            <v-btn
              class="mt-3 upload-card__button"
              small
              plain
              :disabled="isDragOver"
              :ripple="false"
              @click="$refs.uploader.click()"
            >
              <p class="text--text">
                or
                <span type="text" class="primary--text"> Browse </span>
                from your files
              </p>
            </v-btn>
            <input
              ref="uploader"
              :multiple="multiple"
              :accept="`${accept};capture=camera`"
              class="d-none"
              type="file"
              @change="onManualUpload"
            />
          </div>
        </v-card-text>
        <v-card-actions class="px-0">
          <span class="gray--text font-weight-medium">
            {{ $t('@AcceptedFiles').replace('@files', fileFormat) }}
          </span>
        </v-card-actions>
      </v-col>
      <v-col v-if="files.length" cols="12" :sm="multiple ? 6 : 12">
        <CfsPerfectScrollbar class="w-full" :height="areaHeight" scroll-y>
          <v-card-text class="pl-0 w-full">
            <div class="d-flex flex-wrap w-full justify-space-between">
              <div
                v-for="(file, index) in files"
                :key="file.name"
                class="d-flex align-center w-full mb-2 uploaded-file"
              >
                <div class="py-3 wild-sand min-w-[354px] w-[354px] rounded-lg">
                  <v-list-item class="pl-0 pr-4">
                    <CfsIcon icon-name="document" max-width="4rem" />
                    <v-list-item-content>
                      <v-list-item-title class="mb-1 text--text uploaded-file__title">
                        {{ file.name || file.filename }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text--text mb-1 uploaded-file__subtitle">
                        <span>{{ file.size || file.fileSize | filesize }} </span>
                        <span class="ml-2">{{ getFileType(file) }}</span>
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        v-if="showPreview"
                        class="cursor-pointer primary--text"
                        @click="onShowPreview(file)"
                      >
                        Show preview
                      </v-list-item-subtitle>
                      <v-list-item-subtitle
                        v-if="openPreview"
                        class="select-none"
                        :class="{ 'cursor-pointer primary--text': !!file?.url }"
                        @click="onOpenPreview(file)"
                      >
                        Show preview
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </div>
                <v-btn
                  :disabled="readonly"
                  icon
                  class="mx-1"
                  color="error"
                  @click="onClickDeleteFile(index)"
                >
                  <v-icon> mdi-close-circle</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </CfsPerfectScrollbar>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { isObject } from '@/helpers/objects';
import { useToast } from '@/composables/useToast';
import i18n from '@/i18n';
import CfsReadonlyFieldComponent from '@/framework/inputs/special/CfsReadonlyFieldComponent.vue';
import { AttachmentEntity } from '@/types/common/attachment.entity';
import { useToggle } from '@/composables/useToggle';

export default defineComponent({
  components: { CfsReadonlyFieldComponent },
  model: {
    prop: 'modelValue',
    event: 'update:model-value',
  },
  props: {
    modelValue: {
      type: [Object, Array, File] as PropType<
        AttachmentEntity | AttachmentEntity[] | File | File[] | null
      >,
      default: null,
    },
    areaHeight: {
      type: String,
      default: '174px',
    },
    title: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    accept: {
      type: String,
      default: '*',
    },
    fileFormat: {
      type: String,
      default: 'Any',
    },
    showPreview: {
      type: Boolean,
      default: false,
    },
    openPreview: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:model-value', 'preview'],
  setup(props, { emit, expose }) {
    const toast = useToast();
    const [isDragOver, toggleDragOver] = useToggle();

    const files = computed<(AttachmentEntity | File)[]>(() => {
      if (!props.modelValue) return [];

      if (isObject(props.modelValue) && !Array.isArray(props.modelValue)) {
        return [props.modelValue];
      }
      return props.modelValue;
    });

    const uploadFiles = (fileList: FileList) => {
      if (!props.multiple && fileList.length > 1) {
        return toast.error(i18n.tc('OnlyOneFileCanBeUploaded'));
      }

      if ([...fileList].some((file) => file?.size <= 0))
        return toast.error("Can't upload empty files!");

      const sortedFilesByName = [...files.value, ...fileList].sort((a, b) => {
        if (a instanceof File && b instanceof File) {
          return a.name.localeCompare(b.name);
        }
        if (a instanceof File && !(b instanceof File)) {
          return a.name.localeCompare(b.filename);
        }
        if (!(a instanceof File) && b instanceof File) {
          return a.filename.localeCompare(b.name);
        }
        if (!(a instanceof File) && !(b instanceof File)) {
          return a.filename.localeCompare(b.filename);
        }
        return -1;
      });

      const updatedFiles = props.multiple ? sortedFilesByName : fileList[0];
      emit('update:model-value', updatedFiles);
    };

    const onManualUpload = (e: Event) => {
      const target = e.target as HTMLInputElement;

      if (!target.files) return;

      uploadFiles(target.files);
    };

    const onClickDeleteFile = (index: number) => {
      if (props.readonly) return;

      const filteredFiles = files.value.filter((_, fileIndex) => index !== fileIndex);
      const updatedFiles = props.multiple ? filteredFiles : null;
      emit('update:model-value', updatedFiles);
    };

    const onDropFiles = (e: DragEvent) => {
      toggleDragOver(false);

      if (!e.dataTransfer) return;

      uploadFiles(e.dataTransfer.files);
    };

    const isFile = (file: File | AttachmentEntity): file is File => {
      return 'type' in file;
    };
    const getFileType = (file: File | AttachmentEntity) => {
      const fileType = isFile(file) ? file.type : file.fileType;
      if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return 'XLSX';
      }
      return fileType;
    };

    const onOpenPreview = (file: AttachmentEntity) => {
      if (!file?.url) return;
      window.open(file.url, '_blank').focus();
    };

    const onShowPreview = (file: File | AttachmentEntity) => {
      emit('preview', file);
    };

    expose({
      uploadFiles,
    });

    return {
      files,
      isDragOver,
      onDropFiles,
      getFileType,
      toggleDragOver,
      onManualUpload,
      onClickDeleteFile,
      onOpenPreview,
      onShowPreview,
    };
  },
});
</script>

<style lang="scss" scoped>
.upload-card {
  .upload-content {
    border: 1px dashed #7c7c96;
    border-radius: 10px;

    height: v-bind(areaHeight);

    display: flex;
    align-items: center;
    justify-content: center;

    div {
      :not(button) {
        pointer-events: none;
      }
    }
  }

  .uploaded-file {
    &__title,
    &__subtitle {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .upload-card-title {
    font-weight: 600;
    font-size: 16px;
  }

  &__button {
    ::v-deep .v-btn__content {
      opacity: 1 !important;
    }
  }
}
</style>
